'use client';
import * as React from 'react';
import { cn, getTestId } from '../../utils';
import { useTranslation } from '../../providers/UIProvider';
import { BottomSheet } from '../bottom-sheet/BottomSheet';
import { Button } from '../Button';
import { Icon } from '../icons/Icon';

export interface FilterSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Called when the sheet should be closed */
  onClose: () => void;
  /** Sheet title shown in the header */
  title?: string;
  /** Label for the apply/close button */
  applyLabel?: string;
  /** Replaces the apply footer; `null` hides it */
  footer?: React.ReactNode | null;
  /** Children content (filter controls) */
  children?: React.ReactNode;
  /** Additional CSS classes for the panel */
  className?: string;
  testId?: string;
  id?: string;
}

/**
 * Mobile-optimised filter sheet that slides up from the bottom.
 * Used as the mobile companion to the desktop sidebar filter panel.
 *
 * @example
 * ```tsx
 * <FilterSheet open={open} onClose={() => setOpen(false)} title="Filtry">
 *   <InvoiceFilterPanel filters={filters} onFilterChange={setFilter} />
 * </FilterSheet>
 * ```
 */
export const FilterSheet = React.forwardRef<HTMLDivElement, FilterSheetProps>(
  ({ open, onClose, title, applyLabel, footer, children, className, testId, id }, ref) => {
    const t = useTranslation('components.filterSheet');
    const resolvedTitle = title ?? t('title');
    const resolvedApplyLabel = applyLabel ?? t('applyLabel');
    return (
      <BottomSheet
        ref={ref}
        open={open}
        onClose={onClose}
        testId={getTestId({ testId, id })}
        className={cn('bg-background-base', className)}
      >
        {/* Drag handle */}
        <div className='flex justify-center pt-md pb-xs shrink-0'>
          <div className='w-10 h-1 rounded-full bg-border-base' />
        </div>

        {/* Header */}
        <div className='flex items-center justify-between px-lg py-md border-b border-border-base shrink-0'>
          <BottomSheet.Title className='text-base font-semibold text-foreground-base'>
            {resolvedTitle}
          </BottomSheet.Title>
          <button
            onClick={onClose}
            aria-label={t('close')}
            className='p-sm rounded-md hover:bg-background-input transition-colors'
          >
            <Icon name='xmark' className='!w-4 !h-4 text-foreground-muted' />
          </button>
        </div>

        {/* Scrollable content */}
        <div className='overflow-y-auto flex-1 px-lg py-md space-y-md'>{children}</div>

        {/* Footer */}
        {footer !== null && (
          <div className='shrink-0 px-lg py-md border-t border-border-base'>
            {footer ?? (
              <Button variant='primary' className='w-full' size='md' onClick={onClose}>
                {resolvedApplyLabel}
              </Button>
            )}
          </div>
        )}
      </BottomSheet>
    );
  }
);

FilterSheet.displayName = 'FilterSheet';
